import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import { Box, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import { useIntl } from 'react-intl';

import { Icon, type MessageFeedback } from '@verantos-dev/shared-components';

import { useFeedbackBar } from './useFeedbackBar';

/**
 * FeedbackBar component for rating RA responses.
 *
 * NOTE: Feedback is only available for messages with a messageId.
 * Legacy messages (created before the feedback feature) don't have messageId
 * and will not display the FeedbackBar.
 */
export interface FeedbackBarProps {
  messageId: string;
  feedback: MessageFeedback | null;
  onSubmitFeedback: (
    messageId: string,
    rating: 'thumbs_up' | 'thumbs_down',
    comment?: string
  ) => void;
  onDeleteFeedback: (messageId: string) => void;
  isVisible?: boolean;
  isSubmitting?: boolean;
  isDeleting?: boolean;
  /** When true, always shows the feedback bar. When false, only shows on hover. */
  isLatestRAMessage?: boolean;
  /** Passed from parent message container when the whole message is hovered. */
  isMessageHovered?: boolean;
}

export const FeedbackBar = ({
  messageId,
  feedback,
  onSubmitFeedback,
  onDeleteFeedback,
  isVisible = true,
  isSubmitting = false,
  isDeleting = false,
  isLatestRAMessage = false,
  isMessageHovered = false,
}: FeedbackBarProps) => {
  const intl = useIntl();
  const {
    selectedRating,
    showCommentBox,
    comment,
    shouldRender,
    isBusy,
    isSubmitDisabled,
    handleRatingClick,
    handleSubmit,
    handleEdit,
    handleCommentChange,
    handleCommentBlur,
  } = useFeedbackBar({
    messageId,
    feedback,
    onSubmitFeedback,
    onDeleteFeedback,
    isVisible,
    isSubmitting,
    isDeleting,
  });

  if (!shouldRender) {
    return null;
  }

  const positiveTooltipId =
    selectedRating === 'thumbs_up'
      ? 'projects.researchAssistant.feedback.clearRating'
      : 'projects.researchAssistant.feedback.tooltipPositive';
  const negativeTooltipId =
    selectedRating === 'thumbs_down'
      ? 'projects.researchAssistant.feedback.clearRating'
      : 'projects.researchAssistant.feedback.tooltipNegative';
  const positiveTooltipTitle = isBusy
    ? ''
    : intl.formatMessage({
        id: positiveTooltipId,
      });
  const shouldShowNegativeTooltip =
    !isBusy && !(selectedRating === 'thumbs_down' && showCommentBox);
  const negativeTooltipTitle = shouldShowNegativeTooltip
    ? intl.formatMessage({
        id: negativeTooltipId,
      })
    : '';

  // Re-clicking an active rating clears it (no separate clear button per UX).
  const shouldHighlight = isLatestRAMessage || isMessageHovered;

  return (
    <Box sx={{ mt: 1.5, minHeight: 32, position: 'relative', px: 3 }} data-testid="feedback-bar">
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: 1,
          opacity: isBusy ? 0.5 : 1,
          transition: 'opacity 0.15s ease-in-out',
        }}
        data-highlight={shouldHighlight ? 'true' : 'false'}
      >
        <Tooltip
          title={positiveTooltipTitle}
          arrow
          disableFocusListener
          disableHoverListener={isBusy}
          disableTouchListener={isBusy}
        >
          <span>
            <IconButton
              size="small"
              onClick={() => handleRatingClick('thumbs_up')}
              disabled={isBusy}
              sx={{
                color: 'text.primary',
                borderRadius: (theme) => theme.spacing(1),
              }}
              aria-label={intl.formatMessage({
                id: positiveTooltipId,
              })}
              data-testid="feedback-thumbs-up"
            >
              {selectedRating === 'thumbs_up' ? (
                <ThumbUpIcon sx={{ width: 16, height: 16 }} />
              ) : (
                <ThumbUpOutlinedIcon sx={{ width: 16, height: 16 }} />
              )}
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip
          title={negativeTooltipTitle}
          arrow
          disableFocusListener
          disableHoverListener={!shouldShowNegativeTooltip}
          disableTouchListener={!shouldShowNegativeTooltip}
        >
          <span>
            <IconButton
              size="small"
              onClick={() => handleRatingClick('thumbs_down')}
              disabled={isBusy}
              sx={{
                color: 'text.primary',
                borderRadius: (theme) => theme.spacing(1),
              }}
              aria-label={intl.formatMessage({
                id: negativeTooltipId,
              })}
              data-testid="feedback-thumbs-down"
            >
              {selectedRating === 'thumbs_down' ? (
                <ThumbDownIcon sx={{ width: 16, height: 16 }} />
              ) : (
                <ThumbDownOutlinedIcon sx={{ width: 16, height: 16 }} />
              )}
            </IconButton>
          </span>
        </Tooltip>

        {selectedRating === 'thumbs_down' && !showCommentBox && !isBusy && (
          <Tooltip
            title={intl.formatMessage({
              id: 'projects.researchAssistant.feedback.editTooltip',
            })}
            arrow
          >
            <IconButton
              size="small"
              onClick={handleEdit}
              disabled={isBusy}
              sx={{ color: 'text.primary', ml: 0.5, borderRadius: (theme) => theme.spacing(1) }}
              aria-label={intl.formatMessage({
                id: 'projects.researchAssistant.feedback.editTooltip',
              })}
              data-testid="feedback-edit"
            >
              <Icon id={`edit-feedback-${messageId}`} icon="edit" height={16} width={16} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {showCommentBox && (
        <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <TextField
            fullWidth
            multiline
            rows={3}
            size="small"
            autoFocus
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: { maxLength: 1000 },
            }}
            label={intl.formatMessage({
              id: 'projects.researchAssistant.feedback.commentLabel',
            })}
            placeholder={intl.formatMessage({
              id: 'projects.researchAssistant.feedback.commentPlaceholder',
            })}
            value={comment}
            onChange={(e) => handleCommentChange(e.target.value)}
            onBlur={handleCommentBlur}
            data-testid="feedback-comment-input"
          />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              pt: 0.5,
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ pl: 3 }}>
              {`${comment.length}/1000`}
            </Typography>
            <Tooltip
              title={intl.formatMessage({
                id: 'projects.researchAssistant.feedback.submitButton',
              })}
              arrow
              disableHoverListener={isSubmitDisabled}
              disableFocusListener={isSubmitDisabled}
              disableTouchListener={isSubmitDisabled}
            >
              <span>
                <IconButton
                  size="small"
                  onClick={handleSubmit}
                  disabled={isSubmitDisabled}
                  data-testid="feedback-submit"
                  sx={{
                    borderRadius: (theme) => theme.spacing(0.5),
                    '&:hover': {
                      borderRadius: (theme) => theme.spacing(0.5),
                    },
                  }}
                >
                  <CheckCircleOutlineIcon sx={{ width: 16, height: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
      )}
    </Box>
  );
};
